import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { CALENDAR_BREAKPOINT, SP_BREAKPOINT } from "../../constants/app";
import {
  PRIMARY_DARK_GREEN,
  PRIMARY_LIGHT_RED,
  PRIMARY_RED,
} from "../../constants/color";
import { formatDateTime } from "../../utils/date";
import { Calendar } from "./Calendar";
import { RequiredBadge } from "./RequiredBadge";
import { ErrorIcon } from "./icons/input/ErrorIcon";

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  defaultValue?: Date;
  defaultCalendarValue?: Date;
  disabled?: boolean;
  onChange?: (date: Date) => void;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
};

export const DatePicker: React.FC<Props> = ({
  name,
  label,
  placeholder = "--",
  required,
  defaultValue,
  defaultCalendarValue,
  disabled,
  onChange,
  hasError,
  errorMessage,
  width = "320px",
}) => {
  const uuid = useMemo(() => {
    return uuidv4();
  }, []);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentDateTime, setCurrentDateTime] = useState<Date | undefined>(
    defaultValue,
  );

  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  useEffect(() => {
    if (!currentDateTime) return;

    setValue?.(
      name,
      formatDateTime(currentDateTime, false, "YYYY/MM/DD HH:mm"),
      { shouldValidate: true },
    );
  }, [currentDateTime]);

  const handleOnSelect = useCallback((date: Date, time: number) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(time);
    selectedDate.setMinutes(0);
    selectedDate.setSeconds(0);
    selectedDate.setMilliseconds(0);

    setCurrentDateTime(selectedDate);
    setIsOpen(false);
    onChange?.(selectedDate);
  }, []);

  const handleOpenSelection = useCallback(() => {
    if (disabled) return;

    setIsOpen((prev) => !prev);
  }, [disabled]);

  const closeDropdown = useCallback((e: MouseEvent) => {
    // @ts-expect-error
    const id = e.target?.id ?? "";
    // @ts-expect-error
    const nodeName = e.target?.nodeName ?? "";

    if (
      id.includes(uuid) ||
      ["table", "thead", "tbody", "th", "tr", "td"].includes(
        nodeName.toLowerCase(),
      )
    )
      return;

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) document.body.addEventListener("click", closeDropdown);
    else document.body.removeEventListener("click", closeDropdown);

    return () => {
      document.body.removeEventListener("click", closeDropdown);
    };
  }, [isOpen]);

  useEffect(() => {
    const subscription = formContext?.watch((value, { name: fieldName }) => {
      // Handle setField Methods
      if (fieldName === name && !value[name]) {
        return setCurrentDateTime(value[name] ?? undefined);
      }
    });
    return () => subscription?.unsubscribe();
  }, [formContext]);

  return (
    <StyledContainer
      id={`container-${uuid}`}
      $disabled={disabled}
      $width={width}
    >
      <StyledHeader>
        {label && <StyledLabel>{label}</StyledLabel>}
        {required && <RequiredBadge />}
      </StyledHeader>

      <StyledSelect
        id={`select-container-${uuid}`}
        onClick={handleOpenSelection}
        $disabled={disabled}
        $hasError={hasError}
      >
        <StyledPlaceholder
          id={`select-placeholder-${uuid}`}
          $isPlaceholder={!currentDateTime}
        >
          {currentDateTime
            ? formatDateTime(currentDateTime, false, "YYYY/MM/DD HH:mm")
            : placeholder}
        </StyledPlaceholder>
      </StyledSelect>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}

      <StyledCalendarContainer $isOpen={isOpen && !disabled} $width={width}>
        <Calendar
          uuid={uuid}
          defaultDate={defaultValue ?? defaultCalendarValue ?? undefined}
          defaultTime={
            defaultValue
              ? defaultValue?.getHours()
              : defaultCalendarValue
                ? defaultCalendarValue.getHours()
                : undefined
          }
          onCancel={() => setIsOpen(false)}
          onSelect={handleOnSelect}
        />
      </StyledCalendarContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $disabled?: boolean; $width: string }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
  }

  width: ${({ $width }) => $width};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledSelect = styled.div<{
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: 100%;
  height: 48px;
  padding: 17px 12px 17px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;

  ${({ $disabled, $hasError }) =>
    `
      border-color: ${
        $disabled ? "#cccccc" : $hasError ? PRIMARY_RED : "#cccccc"
      };
      background-color: ${
        $disabled ? "#f2f2f2" : $hasError ? PRIMARY_LIGHT_RED : "#ffffff"
      };
    `}

  &:focus {
    border-color: ${({ $hasError }) =>
      $hasError ? PRIMARY_RED : PRIMARY_DARK_GREEN};
  }
`;

const StyledPlaceholder = styled.div<{
  $isPlaceholder: boolean;
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: 170%;
  letter-spacing: 0.56px;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? "#999999" : "#000000")};
`;

const rotate = ($isOpen: boolean | null) => keyframes`
  from {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 0 : 180) + "deg"});
  }
  to {
    transform: rotate(${($isOpen === null ? 0 : $isOpen ? 180 : 0) + "deg"});
  }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean | null }>`
  transform: ${({ $isOpen }) =>
    $isOpen === null
      ? "rotate(0deg)"
      : $isOpen
        ? "rotate(180deg)"
        : "rotate(0deg)"};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${PRIMARY_RED};
`;

const StyledCalendarContainer = styled.div<{
  $width: string;
  $isOpen: boolean;
}>`
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: ${CALENDAR_BREAKPOINT}) {
    max-width: 210px;
    max-height: ${({ $isOpen }) => ($isOpen ? "450px" : "0")};
  }

  position: absolute;
  top: 110%;
  width: ${({ $width }) => $width};
  max-width: 320px;
  max-height: ${({ $isOpen }) => ($isOpen ? "320px" : "0")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.24);
  transition: max-height 0.2s ease-in-out;
  z-index: 100;
  left: auto;
  right: 0;
`;
